import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import { required } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import router from "@/router";
import axios from "@axios";
import { reactive, toRefs, ref } from "@vue/composition-api";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { v4 as uuidv4 } from "uuid";
import { fieldStructure } from "@/components/securitySelfAuditTemplateBuilder/config";

export default function useTemplateBuilderConfig() {
  const state = reactive({
    items: [
      {
        name: "",
        type: "facility",
        logic: [],
        errors: {},
        item_id: "facility",
        options: { required: false, fieldTypeCfg: { data: [] } },
        hasErrors: false,
        isDefault: true,
      },
    ],
    isValid: false,
    templateId: null,
    isUpdating: false,
  });

  const templateName = ref("");
  const idTenant = ref(null);

  const toast = useToast();
  const { refFormObserver, getValidationState } = formValidation();
  const { t } = useI18nUtils();
  const trans = {
    "messages.edit_save.successfully_created": t("messages.edit_save.successfully_created"),
    "messages.edit_save.updated_successfully": t("messages.edit_save.updated_successfully"),
  };

  const addItem = () => {
    const item = JSON.parse(JSON.stringify(fieldStructure));
    item.item_id = uuidv4();
    state.items.push(item);
  };

  const setItems = (value) => {
    state.items = Array.isArray(value) ? value : state.items;
  };

  const setIsValid = (value) => {
    state.isValid = Boolean(value);
  };

  const pickId = (responseData, fullResponse) => {
    const candidateIds = [
      responseData?.id,
      responseData?.data?.id,
      responseData?.id_template,
      responseData?.data?.id_template,
      responseData?.template?.id,
    ];
    const foundId = candidateIds.find((value) => value != null);
    if (!foundId && fullResponse?.headers?.location) {
      const matchedRoute = String(fullResponse.headers.location).match(/\/builder\/(\d+)(?:$|\/|\?)/);
      if (matchedRoute && matchedRoute[1]) return String(matchedRoute[1]);
    }
    return foundId ? String(foundId) : null;
  };

  const storeTemplate = async (name, data, extra = {}) => {
    const payload = { name, data, ...extra };

    if (!state.isUpdating) {
      const response = await axios.post("v1/bau_complement_templates", payload);
      const responseData = response.data;

      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.edit_save.successfully_created"],
          icon: "CheckIcon",
          variant: "success",
        },
      });

      const newId = pickId(responseData, response);
      if (newId) {
        state.isUpdating = true;
        state.templateId = newId;
        router.replace({ name: "configBauComplementTemplateEdit", params: { id_template: newId } });
      } else {
        toast({
          component: ToastificationContent,
          props: { title: "Guardado, pero no llegó el ID.", icon: "AlertTriangleIcon", variant: "warning" },
        });
      }
      return;
    }

    const response = await axios.put(`v1/bau_complement_templates/${state.templateId}`, payload);
    const responseData = response.data;

    toast({
      component: ToastificationContent,
      props: {
        title: trans["messages.edit_save.updated_successfully"],
        icon: "CheckIcon",
        variant: "success",
      },
    });

    const ensuredId = pickId(responseData, response) || state.templateId;
    state.templateId = ensuredId;
    const currentRoute = router.currentRoute;
    if (
      currentRoute.name !== "configBauComplementTemplateEdit" ||
      currentRoute.params.id_template !== String(ensuredId)
    ) {
      router.replace({ name: "configBauComplementTemplateEdit", params: { id_template: String(ensuredId) } });
    }
  };

  const save = async (extra = {}, itemsOverride = null) => {
    const isFormValid = await refFormObserver.value.validate();
    if (!isFormValid) return;
    const dataToStore = Array.isArray(itemsOverride) ? itemsOverride : state.items;
    await storeTemplate(templateName.value, dataToStore, extra);
  };

  const initFromRoute = async () => {
    const idTemplate = router.currentRoute.params.id_template || router.currentRoute.params.id || null;
    if (!idTemplate) return;

    state.isUpdating = true;
    state.templateId = String(idTemplate);

    const { data } = await axios.get(`v1/bau_complement_templates/${idTemplate}`);

    templateName.value = data.name || "";
    if (Array.isArray(data.data)) state.items = data.data;

    if (data.id_tenant) idTenant.value = data.id_tenant;
    else if (data.tenant && data.tenant.id) idTenant.value = data.tenant.id;
  };

  return {
    addItem,
    save,
    setItems,
    setIsValid,
    ...toRefs(state),
    templateName,
    idTenant,
    refFormObserver,
    getValidationState,
    required,
    initFromRoute,
  };
}
